import {
  EAP_FACE_TO_FACE_VISITS_LIMIT_TYPE_LABELS,
  EMPLOYER_CONTRIBUTION_LABELS,
  enumLabel,
  enumLabelOrTitleCase,
  formatAgeReductionSchedule,
  formatCurrency,
  formatDefinitionOfEarnings,
  formatEligibilityHours,
  formatMultiplier,
  formatRateGuarantee,
  INCLUSION_LABELS,
  isSentinel,
  PORTABILITY_LABELS,
  sentinelLabel,
} from "./shared";

export type BasicLifeFieldFormatter = (row: Record<string, unknown>) => string;

/**
 * Benefit Amount — increments/dollarAmount/salaryMultiplier/salaryMultiplierPlusDollarAmount.
 * Amount siblings: benefitAmountDollarAmount, benefitAmountSalaryMultiplier.
 */
const formatBenefitAmount: BasicLifeFieldFormatter = (row) => {
  const type = row["benefitAmountType"];
  if (isSentinel(type)) return sentinelLabel(type as string);
  if (type === "dollarAmount") {
    const dollar = formatCurrency(row["benefitAmountDollarAmount"]);
    return dollar ? `Flat ${dollar}` : "";
  }
  if (type === "increments") {
    const dollar = formatCurrency(row["benefitAmountDollarAmount"]);
    return dollar ? `${dollar} increments` : "";
  }
  if (type === "salaryMultiplier")
    return formatMultiplier(row["benefitAmountSalaryMultiplier"]) || "";
  if (type === "salaryMultiplierPlusDollarAmount") {
    const mult = formatMultiplier(row["benefitAmountSalaryMultiplier"]);
    const dollar = formatCurrency(row["benefitAmountDollarAmount"]);
    if (mult && dollar) return `${mult} up to ${dollar}`;
    return mult || dollar || "";
  }
  return "";
};

/**
 * Benefit Maximum — dollarAmount/salaryMultiplier/lesserOfDollarAmountOrSalaryMultiplier.
 * Amount siblings: benefitMaximumDollarAmount, benefitMaximumSalaryMultiplier.
 */
const formatBenefitMaximum: BasicLifeFieldFormatter = (row) => {
  const type = row["benefitMaximumType"];
  if (isSentinel(type)) return sentinelLabel(type as string);
  if (type === "dollarAmount")
    return formatCurrency(row["benefitMaximumDollarAmount"]) || "";
  if (type === "salaryMultiplier")
    return formatMultiplier(row["benefitMaximumSalaryMultiplier"]) || "";
  if (type === "lesserOfDollarAmountOrSalaryMultiplier") {
    const dollar = formatCurrency(row["benefitMaximumDollarAmount"]);
    const mult = formatMultiplier(row["benefitMaximumSalaryMultiplier"]);
    if (dollar && mult) return `Lesser of ${dollar} or ${mult}`;
    return dollar || mult || "";
  }
  return "";
};

/**
 * Guarantee Issue — dollarAmount/salaryMultiplierWithCap/
 * salaryMultiplierWithDollarAmountPlusAdditionalAmount/allGuaranteeIssue/notIncluded.
 * Siblings: guaranteeIssueDollarAmount, guaranteeIssueSalaryMultiplier, guaranteeIssueAdditionalAmount.
 */
const formatGuaranteeIssue: BasicLifeFieldFormatter = (row) => {
  const type = row["guaranteeIssueType"];
  if (isSentinel(type)) return sentinelLabel(type as string);

  if (type === "allGuaranteeIssue") return "All Amounts";
  if (type === "dollarAmount")
    return formatCurrency(row["guaranteeIssueDollarAmount"]) || "";
  if (type === "salaryMultiplierWithCap") {
    const mult = formatMultiplier(row["guaranteeIssueSalaryMultiplier"]);
    const cap = formatCurrency(row["guaranteeIssueDollarAmount"]);
    if (mult && cap) return `${mult} up to ${cap}`;
    return mult || "";
  }
  if (type === "salaryMultiplierWithDollarAmountPlusAdditionalAmount") {
    const mult = formatMultiplier(row["guaranteeIssueSalaryMultiplier"]);
    const dollar = formatCurrency(row["guaranteeIssueDollarAmount"]);
    const additional = formatCurrency(row["guaranteeIssueAdditionalAmount"]);
    const base =
      mult && dollar ? `${mult} up to ${dollar}` : mult || dollar || "";
    return additional ? `${base} + ${additional}` : base;
  }
  return "";
};

/** Spouse/Child benefit — dollarAmount or notIncluded. */
const dependentBenefit =
  (typeKey: string, amountKey: string): BasicLifeFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type as string);

    if (type === "dollarAmount") return formatCurrency(row[amountKey]) || "";
    return "";
  };

/**
 * Waiver of Premium's eligibility age, waiting period and duration — extended
 * (non-v5) fields the analyst enters as a bare integer through a numeric cell
 * (CD-2412). The grid wraps that integer in the row's prefix or suffix, so
 * "60" reads as "Age 60" and "88" as "88 months"; the sheet has to match what
 * the analyst is looking at.
 *
 * A value that is already decorated, or any other text a plan quoted before
 * CD-2412 still carries, passes through untouched — the grid decorates the
 * same way, blindly, and blanking it here would drop the row from the sheet.
 */
const decoratedValue =
  (
    fieldKey: string,
    { prefix = "", suffix = "" }: { prefix?: string; suffix?: string },
  ): BasicLifeFieldFormatter =>
  (row) => {
    const value = row[fieldKey];
    if (typeof value !== "string" && typeof value !== "number") return "";

    const text = String(value).trim();
    if (text === "") return "";
    if (isSentinel(text)) return sentinelLabel(text);
    if (!/^\d+$/.test(text)) return text;

    return `${prefix}${text}${suffix}`;
  };

/**
 * EAP - Number of Face to Face Visits. The grid splits this into two cells, a
 * visit count and a limit type, which carry the same mpConfigKey because the
 * template has one row for the pair; the sheet reads them back as one value,
 * count first ("5 Per Lifetime"). Either half alone stands on its own, so a
 * plan that only recorded "Unlimited" still fills the row.
 */
const formatEapFaceToFaceVisits: BasicLifeFieldFormatter = (row) => {
  const count = decoratedValue("eapVisitCount", {})(row);
  const limitType = enumLabelOrTitleCase(
    "eapFaceToFaceVisitsLimitType",
    EAP_FACE_TO_FACE_VISITS_LIMIT_TYPE_LABELS,
  )(row);

  return [count, limitType].filter(Boolean).join(" ");
};

export const basicLifeFormatters: Record<string, BasicLifeFieldFormatter> = {
  benefitAmount: formatBenefitAmount,
  benefitMaximum: formatBenefitMaximum,
  guaranteeIssue: formatGuaranteeIssue,
  spouseBenefit: dependentBenefit("spouseBenefitType", "spouseBenefitAmount"),
  childBenefit: dependentBenefit("childBenefitType", "childBenefitAmount"),
  ageReductionSchedule: formatAgeReductionSchedule,
  definitionOfEarnings: formatDefinitionOfEarnings,
  portability: enumLabel("portability", PORTABILITY_LABELS),
  conversion: enumLabel("conversion", INCLUSION_LABELS),
  acceleratedDeathBenefit: enumLabel(
    "acceleratedDeathBenefit",
    INCLUSION_LABELS,
  ),
  waiverOfPremium: enumLabel("waiverOfPremium", INCLUSION_LABELS),
  employerContribution: enumLabel(
    "employerContribution",
    EMPLOYER_CONTRIBUTION_LABELS,
  ),
  rateGuarantee: formatRateGuarantee,
  // Extended (non-v5) fields — CD-1662, carried on `benefits.extended`.
  eligibilityHours: formatEligibilityHours,
  waiverOfPremiumEligibility: decoratedValue("waiverOfPremiumEligibility", {
    prefix: "Age ",
  }),
  waiverOfPremiumWaitingPeriod: decoratedValue("waiverOfPremiumWaitingPeriod", {
    suffix: " months",
  }),
  waiverOfPremiumDuration: decoratedValue("waiverOfPremiumDuration", {
    prefix: "Age ",
  }),
  airBag: enumLabelOrTitleCase("airBag", INCLUSION_LABELS),
  commonCarrier: enumLabelOrTitleCase("commonCarrier", INCLUSION_LABELS),
  repatriation: enumLabelOrTitleCase("repatriation", INCLUSION_LABELS),
  seatBelt: enumLabelOrTitleCase("seatBelt", INCLUSION_LABELS),
  eapFaceToFaceVisits: formatEapFaceToFaceVisits,
  travelAid: enumLabelOrTitleCase("travelAid", INCLUSION_LABELS),
};

export const formatBasicLifeField = (
  fieldId: string,
  row: Record<string, unknown>,
): string | undefined => {
  const formatter = basicLifeFormatters[fieldId];
  return formatter ? formatter(row) : undefined;
};
